/**
 * Generic Response Display Component
 * Configurable display for API response data across all pages
 */

import React from 'react';

/**
 * Configuration for displaying a single field
 */
export interface FieldConfig<T = unknown> {
  type?: 'field' | 'subheader'; // Type of field (default: 'field')
  key?: keyof T; // Field key in response object (optional if using getValue)
  label: React.ReactNode; // Display label (supports JSX for superscripts, subscripts, etc.)
  formatter?: (value: unknown) => React.ReactNode; // Optional custom formatter
  getValue?: (data: T) => React.ReactNode;
  show?: (data: T) => boolean; // Optional condition to show/hide field
}

/**
 * Configuration for a section of fields
 */
export interface SectionConfig<T = unknown> {
  title: string;
  fields: FieldConfig<T>[];
}

/**
 * Props for ResponseDisplay component
 */
export interface ResponseDisplayProps<T = unknown> {
  data: T | null;
  sections: SectionConfig<T>[];
  emptyMessage?: string;
  className?: string;
}

/**
 * Generic Response Display Component
 */
export function ResponseDisplay<T = unknown>({ data, sections, emptyMessage = 'No analysis results yet.', className = '' }: ResponseDisplayProps<T>) {
  if (!data) {
    return <div className="p-4 text-black-500 italic">{emptyMessage}</div>;
  }

  const renderValue = (field: FieldConfig<T>) => {
    const value = field.getValue ? field.getValue(data) : data[field.key!];

    if (field.formatter) return field.formatter(value);

    // ✅ If value is already a React node, render it
    if (React.isValidElement(value)) return value;
    if (Array.isArray(value)) return <>{value}</>;

    // Default formatters
    if (typeof value === 'number') return value.toFixed(3);
    if (typeof value === 'string') return value;
    if (value == null) return 'N/A';

    return String(value);
  };

  return (
    <div className={`p-1 h-full overflow-y-auto ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sections.map((section, sectionIdx) => (
          <div key={section.title || `section-${sectionIdx}`} className="bg-white rounded-lg p-4 border">
            <h3 className="text-xs font-bold text-black-500 uppercase tracking-wider mb-3 border-b pb-2">{section.title}</h3>

            <div className="grid grid-cols-2 gap-x-1 gap-y-1 text-sm md:grid-cols-[2fr_3fr]">
              {section.fields.map((field, fieldIdx) => {
                // Check if field should be shown
                const shouldShow = field.show ? field.show(data) : true;
                if (!shouldShow) return null;

                // Render subheader
                if (field.type === 'subheader') {
                  // Look ahead to see if any fields under this subheader are visible
                  const nextSubheaderIdx = section.fields.findIndex((f, idx) => idx > fieldIdx && f.type === 'subheader');
                  const fieldsUntilNextSubheader = section.fields.slice(fieldIdx + 1, nextSubheaderIdx === -1 ? undefined : nextSubheaderIdx);

                  const hasVisibleFields = fieldsUntilNextSubheader.some(f => {
                    const fShow = f.show ? f.show(data) : true;
                    return fShow && f.type !== 'subheader';
                  });

                  if (!hasVisibleFields) return null;

                  return (
                    <div key={`subheader-${sectionIdx}-${fieldIdx}`} className="col-span-2 mt-2 mb-1 pt-2 border-t border-gray-300">
                      <span className="text-xs font-bold text-black-500 uppercase tracking-wide">{field.label}</span>
                    </div>
                  );
                }

                // Render regular field
                return (
                  <React.Fragment key={field.key ? String(field.key) : `field-${sectionIdx}-${fieldIdx}`}>
                    <span className="text-black-600 font-semibold text-[15px]">{field.label}:</span>
                    <span className="font-mono text-right text-[15px]">{renderValue(field)}</span>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
